import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  ArrowLeft, Calendar, MapPin, Clock, Users, GraduationCap, User,
  Award, Trash2, Edit, CheckCircle, Plus, X, Star,
} from "lucide-react"
import {
  useTrainingSession, useDeleteTrainingSession, useCompleteTraining,
  useIssueCertificates, useAddParticipant, useUpdateParticipant, useRemoveParticipant,
} from "@/hooks/use-training"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "sonner"
import type { TrainingParticipant, TrainingStatus, TrainingType, ParticipantStatus } from "@/lib/types"

const STATUS_CONFIG: Record<TrainingStatus, { label: string; color: string }> = {
  planned: { label: "Zaplanowane", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  in_progress: { label: "W trakcie", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  completed: { label: "Zakończone", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
  cancelled: { label: "Anulowane", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300" },
}

const TYPE_LABELS: Record<TrainingType, string> = {
  internal: "Wewnętrzne",
  external: "Zewnętrzne",
  online: "Online",
  certification: "Certyfikacja",
}

const PARTICIPANT_STATUS_CONFIG: Record<ParticipantStatus, { label: string; color: string }> = {
  registered: { label: "Zarejestrowany", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  confirmed: { label: "Potwierdzony", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  attended: { label: "Obecny", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
  no_show: { label: "Nieobecny", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
  cancelled: { label: "Anulowany", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300" },
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function TrainingDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data: sessionData, isLoading } = useTrainingSession(id || "")
  const deleteMutation = useDeleteTrainingSession()
  const completeMutation = useCompleteTraining()
  const issueCertsMutation = useIssueCertificates()
  const addParticipantMutation = useAddParticipant()
  const updateParticipantMutation = useUpdateParticipant()
  const removeParticipantMutation = useRemoveParticipant()

  const [showAddParticipant, setShowAddParticipant] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (isLoading || !sessionData?.data) {
    return (
      <div className="p-6 max-w-[960px] mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  const session = sessionData.data
  const statusCfg = STATUS_CONFIG[session.status]
  const participants = session.participants ?? []

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    deleteMutation.mutate(session.id, {
      onSuccess: () => {
        toast.success("Szkolenie usunięte")
        navigate("/training")
      },
      onError: () => toast.error("Błąd usuwania"),
    })
  }

  const handleComplete = () => {
    completeMutation.mutate(session.id, {
      onSuccess: () => toast.success("Szkolenie oznaczone jako zakończone"),
      onError: () => toast.error("Błąd zmiany statusu"),
    })
  }

  const handleIssueCertificates = () => {
    issueCertsMutation.mutate(session.id, {
      onSuccess: () => toast.success("Certyfikaty wystawione"),
      onError: () => toast.error("Błąd wystawiania certyfikatów"),
    })
  }

  const handleAddParticipant = async () => {
    const input = document.getElementById("participant-user-id") as HTMLInputElement
    if (!input?.value || !id) return
    try {
      await addParticipantMutation.mutateAsync({ sessionId: id, userId: input.value })
      toast.success("Uczestnik dodany")
      setShowAddParticipant(false)
    } catch {
      toast.error("Błąd dodawania uczestnika")
    }
  }

  const handleUpdateParticipantStatus = async (userId: string, status: ParticipantStatus) => {
    if (!id) return
    try {
      await updateParticipantMutation.mutateAsync({ sessionId: id, userId, data: { status } })
      toast.success("Status uczestnika zaktualizowany")
    } catch {
      toast.error("Błąd aktualizacji")
    }
  }

  const handleRemoveParticipant = async (userId: string) => {
    if (!id) return
    try {
      await removeParticipantMutation.mutateAsync({ sessionId: id, userId })
      toast.success("Uczestnik usunięty")
    } catch {
      toast.error("Błąd usuwania")
    }
  }

  return (
    <div className="p-6 max-w-[960px] mx-auto">
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/training")}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Szkolenia
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-foreground">{session.name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {TYPE_LABELS[session.training_type]}
            {session.instructor_name && ` · Prowadzący: ${session.instructor_name}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {session.status !== "completed" && session.status !== "cancelled" && (
            <Button variant="outline" size="sm" onClick={handleComplete} disabled={completeMutation.isPending}>
              <CheckCircle className="h-4 w-4 mr-1" />
              Zakończ
            </Button>
          )}
          {session.status === "completed" && !session.certificates_issued && (
            <Button variant="outline" size="sm" onClick={handleIssueCertificates} disabled={issueCertsMutation.isPending}>
              <Award className="h-4 w-4 mr-1" />
              Wystaw certyfikaty
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/training/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-1" />
            Edytuj
          </Button>
          <Button
            variant={confirmDelete ? "destructive" : "outline"}
            size="sm"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {confirmDelete ? "Potwierdź" : "Usuń"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Szczegóły</TabsTrigger>
          <TabsTrigger value="participants">
            Uczestnicy ({participants.filter(p => p.status !== "cancelled").length})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          {/* Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard
              icon={Calendar}
              label="Termin"
              value={`${formatDateTime(session.starts_at)} — ${formatDateTime(session.ends_at)}`}
            />
            {session.location && (
              <InfoCard icon={MapPin} label="Lokalizacja" value={session.location} />
            )}
            {session.duration_hours && (
              <InfoCard icon={Clock} label="Czas trwania" value={`${session.duration_hours} godz.`} />
            )}
            <InfoCard
              icon={Users}
              label="Uczestnicy"
              value={`${session.participants_count}${session.max_participants ? ` / ${session.max_participants}` : ""}`}
            />
          </div>

          {/* Description */}
          {session.description && (
            <Section title="Opis">
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                {session.description}
              </p>
            </Section>
          )}

          {/* Instructor */}
          {(session.instructor_name || session.instructor_email) && (
            <Section title="Prowadzący">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center bg-muted">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  {session.instructor_name && (
                    <p className="text-sm font-medium text-foreground">{session.instructor_name}</p>
                  )}
                  {session.instructor_email && (
                    <p className="text-xs text-muted-foreground">{session.instructor_email}</p>
                  )}
                </div>
              </div>
            </Section>
          )}

          {/* Cost */}
          {session.cost_per_person && (
            <Section title="Koszt">
              <p className="text-sm text-foreground">
                {new Intl.NumberFormat("pl-PL", { style: "currency", currency: session.currency || "PLN" }).format(session.cost_per_person)} / osoba
              </p>
              {session.participants_count > 0 && (
                <p className="text-xs mt-1 text-muted-foreground">
                  Łączny koszt: {new Intl.NumberFormat("pl-PL", { style: "currency", currency: session.currency || "PLN" }).format(session.cost_per_person * session.participants_count)}
                </p>
              )}
            </Section>
          )}

          {/* Agenda */}
          {session.agenda && (
            <Section title="Agenda">
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                {session.agenda}
              </p>
            </Section>
          )}

          {/* Notes */}
          {session.notes && (
            <Section title="Notatki">
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                {session.notes}
              </p>
            </Section>
          )}

          {/* Certificates */}
          {session.certificates_issued && (
            <Section title="Certyfikaty">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium text-foreground">
                  Certyfikaty wystawione
                </span>
              </div>
            </Section>
          )}
        </TabsContent>

        {/* Participants Tab */}
        <TabsContent value="participants" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Uczestnicy
            </h3>
            {session.status !== "cancelled" && (
              <Button variant="outline" size="sm" onClick={() => setShowAddParticipant(!showAddParticipant)}>
                {showAddParticipant ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                {showAddParticipant ? "Anuluj" : "Dodaj uczestnika"}
              </Button>
            )}
          </div>

          {showAddParticipant && (
            <div className="rounded-lg border p-3 mb-4 space-y-2 bg-muted">
              <p className="text-xs font-medium text-muted-foreground">Podaj ID użytkownika:</p>
              <div className="flex gap-2">
                <input
                  id="participant-user-id"
                  className="flex-1 rounded-md border bg-transparent px-3 py-1.5 text-sm text-foreground outline-none"
                  placeholder="ID użytkownika (UUID)"
                />
                <Button size="sm" onClick={handleAddParticipant} disabled={addParticipantMutation.isPending}>
                  Dodaj
                </Button>
              </div>
            </div>
          )}

          {participants.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Brak uczestników</p>
              <p className="text-xs mt-1 text-muted-foreground">
                Dodaj uczestników do tego szkolenia
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {participants.map((p) => (
                <ParticipantRow
                  key={p.user.id}
                  participant={p}
                  sessionStatus={session.status}
                  onUpdateStatus={(status) => handleUpdateParticipantStatus(p.user.id, status)}
                  onRemove={() => handleRemoveParticipant(p.user.id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function InfoCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3 bg-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-4 bg-card">
      <h3 className="text-sm font-semibold mb-2 text-foreground">{title}</h3>
      {children}
    </div>
  )
}

function ParticipantRow({
  participant,
  sessionStatus,
  onUpdateStatus,
  onRemove,
}: {
  participant: TrainingParticipant
  sessionStatus: TrainingStatus
  onUpdateStatus: (status: ParticipantStatus) => void
  onRemove: () => void
}) {
  const statusCfg = PARTICIPANT_STATUS_CONFIG[participant.status]

  return (
    <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-muted">
        <User className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          {participant.user.first_name} {participant.user.last_name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
          {participant.certificate_issued && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-green-600 dark:text-green-400">
              <Award className="h-3 w-3" />
              {participant.certificate_number}
            </span>
          )}
          {participant.rating && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              {participant.rating}/5
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {sessionStatus !== "cancelled" && participant.status !== "cancelled" && (
          <>
            {participant.status === "registered" && (
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => onUpdateStatus("confirmed")}>
                Potwierdź
              </Button>
            )}
            {(participant.status === "registered" || participant.status === "confirmed") && sessionStatus === "completed" && (
              <>
                <Button variant="ghost" size="sm" className="text-xs h-7 text-green-600" onClick={() => onUpdateStatus("attended")}>
                  Obecny
                </Button>
                <Button variant="ghost" size="sm" className="text-xs h-7 text-red-600" onClick={() => onUpdateStatus("no_show")}>
                  Nieobecny
                </Button>
              </>
            )}
          </>
        )}
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={onRemove}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
